/**
 * One proposed change in the refine review, on the section card's markup (`.section-card`, as the From files review's
 * sections): its title and kind, the **Take** checkbox, the current and proposed text side by side (`DiffPanes`, the
 * changed words in `<del>` and `<ins>`), the editable text for a section, the tagline or the summary, and where it
 * comes from (a quote with the AI marker, or the instruction).
 */

import { useId } from 'react';

import { assetUpdateInSummaryOneMax, assetUpdateInTaglineOneMax, sectionInBodyMax } from '@/api/generated/schemas';
import type { RefineChange } from '@/api/types/refinements';
import { Badge } from '@/ui/Badge';
import { DiffPanes } from '@/ui/DiffPanes';
import { MarkdownEditor } from '@/ui/MarkdownEditor';

import { InstructionNote, SourceNote, type FileNames } from '../files/SourceNote';
import { isEditable, isListChange } from './refineDraft';

export interface ChangeCardProps {
  change: RefineChange;
  /** The current text the proposal is compared with (null: the section does not exist yet). */
  current: string | null;
  /** The text that would be applied: the person's edit, else the proposal. */
  text: string;
  taken: boolean;
  /** The asset's own text moved since the refinement: the current pane shows it now. */
  moved: boolean;
  names: FileNames;
  onTake: (taken: boolean) => void;
  onText: (text: string) => void;
}

function kindWord(change: RefineChange): string {
  if (change.target === 'field') return 'Field';
  return change.current === null ? 'New section' : 'Section';
}

function Editor({ id, change, text, onText }: { id: string; change: RefineChange; text: string; onText: (text: string) => void }) {
  const label = <label htmlFor={id}>Text to apply for {change.title}</label>;
  if (change.target === 'section') {
    return (
      <div className="field">
        {label}
        <MarkdownEditor id={id} value={text} onChange={onText} maxLength={sectionInBodyMax} rows={6} required={false} headingPrefix="### " />
      </div>
    );
  }
  return (
    <div className="field">
      {label}
      {change.key === 'tagline' ? (
        <input id={id} type="text" maxLength={assetUpdateInTaglineOneMax} value={text} onChange={(e) => onText(e.target.value)} />
      ) : (
        <textarea id={id} rows={4} maxLength={assetUpdateInSummaryOneMax} value={text} onChange={(e) => onText(e.target.value)} />
      )}
    </div>
  );
}

function ListLine({ change }: { change: RefineChange }) {
  const parts = [
    change.add.length > 0 ? `Adds ${change.add.join(', ')}` : null,
    change.remove.length > 0 ? `Removes ${change.remove.join(', ')}` : null,
  ].filter(Boolean);
  return <p className="hint">{parts.join('. ')}.</p>;
}

function Sources({ change, names }: { change: RefineChange; names: FileNames }) {
  if (change.sources === 'instruction') return <InstructionNote what={change.title} />;
  return (
    <>
      {change.sources.map((source, i) => (
        <SourceNote key={`${source.file_id}-${i}`} source={source} names={names} what={change.title} verb="proposed" />
      ))}
    </>
  );
}

export function ChangeCard({ change, current, text, taken, moved, names, onTake, onText }: ChangeCardProps) {
  const id = useId();
  return (
    <section className="card section-card section-card-filled" aria-labelledby={`${id}-title`}>
      <div className="section-card-head">
        <h3 id={`${id}-title`} className="section-card-title">{change.title}</h3>
        <Badge tone={change.target === 'field' ? 'neutral' : 'info'}>{kindWord(change)}</Badge>
        {change.target === 'section' && change.audience === 'internal' && <Badge variant="audience" value="internal" />}
        <span className="section-card-tools">
          <label className="check">
            <input type="checkbox" aria-label={`Take ${change.title}`} checked={taken} onChange={(e) => onTake(e.target.checked)} />
            <span className="ck-body">Take</span>
          </label>
        </span>
      </div>
      <div className="section-card-body">
        {moved && (
          <p className="alert alert-warning" role="status">
            {change.title} changed on the asset since the refinement. Current now shows the asset&apos;s text: review the
            proposal against it.
          </p>
        )}
        <DiffPanes current={current ?? ''} proposed={text} />
        {isListChange(change) && <ListLine change={change} />}
        {isEditable(change) && <Editor id={`${id}-text`} change={change} text={text} onText={onText} />}
        <div className="section-card-actions">
          <Sources change={change} names={names} />
        </div>
      </div>
    </section>
  );
}
